// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Lightweight Facade for Cross-Feature Integration
 * 
 * Purpose: Single place for cross-store data composition when needed
 * 
 * Architecture Decision (Task 2, Week 1):
 * - Two-store pattern: vaccineClientStore (decision tree) + vaccineDashboardStore (dashboard)
 * - Facade provides minimal integration layer for cross-store coordination
 * - Direct store access is acceptable for single-feature operations
 * - Facade functions added incrementally only when cross-store needs arise
 * 
 * Guidelines:
 * - Start minimal, add complexity only when needed
 * - Don't add functions "just in case"
 * - Keep functions focused and single-purpose
 * - Document why each function exists (what cross-store need it serves)
 * 
 * Current Status: Skeleton/empty (awaiting actual cross-store needs)
 * 
 * When to Add Functions:
 * - Computation needs data from both stores → Add `useComposedRequestData()`
 * - Scenario updates touch both stores → Add `updateScenario()`
 * - Export needs data from both stores → Add `exportScenario()`
 * - Import needs to update both stores → Add `importScenario()`
 * 
 * When to Re-unify:
 * - Add a unified wrapper only if you repeatedly:
 * - Need transactionality across many features
 * - Compute derived state from >2 stores frequently
 * - Keep duplicating cross-selectors
 * 
 * If that happens, promote facade into root store with feature slices
 * (still accessed through selectors), not a giant "everything" object.
 */

import { useVaccineClientStore } from '../store/vaccineClientStore';
import { useVaccineDashboardStore } from '../store/vaccineDashboardStore';
import { REGIONS, resolveEffectiveTimeHorizon, type VaccineInputs, type ComplicationCategory } from '../components/VaccineDashboard/lib/models';
import { getRegionPopulation, type PopulationMode } from '../data/regions';

// Example placeholder for future cross-store function:
// export const useComposedRequestData = () => {
//   const treeState = useDecisionTreeStore((state) => state.getTreeState());
//   const vaccineConfig = useVaccineDashboardStore((state) => state.vaccineConfig);
//   
//   return {
//     tree: treeState,
//     vaccine: vaccineConfig,
//   };
// };

/**
 * Expand age-banded values (5-year bands) into a per-cycle vector.
 * Cycle k maps to band index floor(k / 5); out-of-range indices clamp to the last band.
 */
export function expandBandsToCycles(bands: number[], cycles: number): number[] {
  if (cycles <= 0 || bands.length === 0) return [];
  const lastIdx = bands.length - 1;
  const out = new Array<number>(cycles);
  for (let k = 0; k < cycles; k++) {
    const idx = Math.floor(k / 5);
    out[k] = bands[Math.min(idx, lastIdx)];
  }
  return out;
}

/**
 * Collapse user-defined complication categories into a single per-cycle EUR
 * cost vector. For each cycle k, returns Σ_c ((incidence_c[k] / 100) * cost_c[k])
 * over enabled complications. Bands are expanded with the same 5-year mapping
 * as `expandBandsToCycles`. Returns an empty array when no enabled
 * complication carries data; callers should treat that as "omit field".
 *
 * @deprecated Use `expandPerCaseTreatmentCostToCycles` instead. The plain
 * Σ rate × cost form double-counts the no-complication baseline when added
 * on top of `cost_infection`, and (because the backend used to express this
 * as a state-time cost in heemod's life-table integration) drifted under
 * cumulative mortality. Retained only so external callers do not break in
 * the same release as the per-case migration.
 */
export function expandComplicationsToCycles(
  complications: ComplicationCategory[] | undefined,
  cycles: number,
): number[] {
  if (cycles <= 0 || !Array.isArray(complications) || complications.length === 0) {
    return [];
  }
  const enabled = complications.filter(
    (c) =>
      c &&
      c.enabled !== false &&
      Array.isArray(c.incidencePctBands) &&
      Array.isArray(c.costPerCaseBands) &&
      c.incidencePctBands.length > 0 &&
      c.costPerCaseBands.length > 0,
  );
  if (enabled.length === 0) return [];
  const out = new Array<number>(cycles).fill(0);
  for (const c of enabled) {
    const incPerCycle = expandBandsToCycles(c.incidencePctBands, cycles);
    const costPerCycle = expandBandsToCycles(c.costPerCaseBands, cycles);
    for (let k = 0; k < cycles; k++) {
      const inc = Math.max(0, Math.min(100, incPerCycle[k] ?? 0)) / 100;
      const cost = Math.max(0, costPerCycle[k] ?? 0);
      out[k] += inc * cost;
    }
  }
  return out;
}

/**
 * Build the per-case treatment cost schedule applied to every incident case.
 *
 * Additive-over-baseline construction (THL Raportti 10/2025 §6.1.4 / Table
 * 6.3 / Figure 7.1):
 *
 *   per_case_cost[k] = baseline
 *                    + hospitalizationRate × hospitalizationCost
 *                    + Σ_c rate_c[k] × (cost_c[k] − baseline)
 *
 * The increment-over-baseline form handles the THL complication overlap
 * correctly (e.g. PHN is a sub-bucket of neuro-total): a case that develops
 * two complications contributes both increments without double-counting the
 * no-complication baseline cost.
 *
 * Hospitalization is folded in as a constant per-case add-on so scenarios
 * that price hospitalization separately keep working; HZ Finland uses
 * hospitalizationCost = 0 and falls back to the pure additive form, which
 * resolves to ~€1,136 per case at age ≥85.
 *
 * The resulting schedule is consumed at the service layer as a flat per-case
 * event cost (cases[t] × schedule[t]), bypassing heemod's life-table
 * integration so the per-case undiscounted cost stays flat across cycles
 * regardless of competing background mortality. This mirrors the QALY
 * Path A treatment of the per-case decrement.
 */
export function expandPerCaseTreatmentCostToCycles(
  baseline: number,
  hospitalizationRate: number,
  hospitalizationCost: number,
  complications: ComplicationCategory[] | undefined,
  cycles: number,
): number[] {
  if (cycles <= 0) return [];
  const safeBaseline = Math.max(0, Number.isFinite(baseline) ? baseline : 0);
  const safeHospRate = Math.max(0, Math.min(1, Number.isFinite(hospitalizationRate) ? hospitalizationRate : 0));
  const safeHospCost = Math.max(0, Number.isFinite(hospitalizationCost) ? hospitalizationCost : 0);
  const hospAddOn = safeHospRate * safeHospCost;
  const out = new Array<number>(cycles).fill(safeBaseline + hospAddOn);
  if (!Array.isArray(complications) || complications.length === 0) return out;
  for (const c of complications) {
    if (!c || c.enabled === false) continue;
    if (!Array.isArray(c.incidencePctBands) || !Array.isArray(c.costPerCaseBands)) continue;
    if (c.incidencePctBands.length === 0 || c.costPerCaseBands.length === 0) continue;
    const incPerCycle = expandBandsToCycles(c.incidencePctBands, cycles);
    const costPerCycle = expandBandsToCycles(c.costPerCaseBands, cycles);
    for (let k = 0; k < cycles; k++) {
      const rate = Math.max(0, Math.min(100, incPerCycle[k] ?? 0)) / 100;
      const cost = Math.max(0, costPerCycle[k] ?? 0);
      out[k] += rate * (cost - safeBaseline);
    }
  }
  // Numerical floor: with extreme inputs the increment term can go negative
  // for an individual cycle (e.g. a complication cheaper than baseline). The
  // schedule represents an event cost, so clamp at 0.
  for (let k = 0; k < cycles; k++) {
    if (!(out[k] >= 0)) out[k] = 0;
  }
  return out;
}

/**
 * Build the `populationParameters` payload from dashboard inputs.
 *
 * Single source of truth for what the backend's `parameters$population_parameters`
 * receives. Both the live-store sync (`prepareComputationData`) and any future
 * direct-request builders should go through here so optional schedule fields
 * (ve / incidence / qaly_loss / background_utility) can never silently drop.
 */
export function buildPopulationParameters(
  inputs: VaccineInputs,
  globalInputs: { populationMode: PopulationMode },
) {
  const effectiveHorizon = resolveEffectiveTimeHorizon(inputs);
  const cycles = Math.max(0, effectiveHorizon - 1);
  const durationInfectionYears = (inputs.durationInfectionDays ?? 7) / 365;

  return {
    population_size: inputs.population,
    coverage_rate:
      calculateAverageCoverage(
        inputs.coveragePctByRegion,
        inputs.selectedRegions,
        inputs.defaultCoveragePct,
      ) / 100,
    age_group: 'all' as const,
    baseline_incidence:
      inputs.baselineIncidencePer100k != null
        ? inputs.baselineIncidencePer100k / 100000
        : 0.05,
    // baseline_mortality = background all-cause mortality (p_death_bg).
    // Separate from case_fatality_rate (infection-specific) below.
    ...(inputs.mortalitySource === 'finnish_lifetable_2023' &&
    typeof inputs.cohortStartingAge === 'number'
      ? {
          mortality_source: 'finnish_lifetable_2023' as const,
          starting_age: inputs.cohortStartingAge,
        }
      : { baseline_mortality: 0 }),
    case_fatality_rate: inputs.baselineMortalityRate ?? 0.001,
    utility_healthy: 1.0,
    utility_infection: inputs.utilityInfection ?? 0.8,
    duration_infection: durationInfectionYears,
    hospitalization_rate: inputs.hospitalizationRate ?? 0.05,
    utility_hospitalization: inputs.utilityHospitalization ?? 0.7,
    duration_hospitalization: (inputs.durationHospitalizationDays ?? 5) / 365,
    waning_rate: (inputs.waningPctPerYear ?? 0) / 100,
    booster_years: inputs.boosterYears ?? [],
    ...(inputs.waningMode === 'schedule' &&
    Array.isArray(inputs.veSchedule) &&
    inputs.veSchedule.length === cycles &&
    inputs.veSchedule.length > 0
      ? {
          ve_schedule: inputs.veSchedule.map((v) =>
            Math.min(1, Math.max(0, v / 100)),
          ),
        }
      : {}),
    // Incidence schedule. Convention: length = effectiveHorizon (Y1..H), with
    // element k (0-indexed JS / 1-indexed R) corresponding to year k+1 at age
    // `cohortStartingAge + k`. Y1 is unused on the backend (Year 1 decision
    // tree uses the scalar `baseline_incidence`); the Markov consumes Y2..H
    // after the backend slices the leading element.
    ...(inputs.incidenceMode === 'schedule' &&
    Array.isArray(inputs.incidenceBandsPer100k) &&
    inputs.incidenceBandsPer100k.length > 0 &&
    typeof inputs.cohortStartingAge === 'number'
      ? {
          incidence_schedule: expandBandsToCycles(
            inputs.incidenceBandsPer100k,
            effectiveHorizon,
          ).map((v) => Math.min(1, Math.max(0, v / 100000))),
        }
      : {}),
    // Per-case QALY loss schedule. Same length-H convention as the other age-
    // banded schedules. Expand 5-year age bands to a per-year vector and
    // un-absorb duration (divide by duration_years) so the backend builder's
    // qaly_loss_inf * duration_infection multiplication recovers the original
    // per-case loss. Y1 unused; Y2..H consumed by the Markov after backend slice.
    ...(inputs.qalyLossMode === 'schedule' &&
    Array.isArray(inputs.qalyLossBands) &&
    inputs.qalyLossBands.length > 0
      ? {
          qaly_loss_schedule: expandBandsToCycles(
            inputs.qalyLossBands,
            effectiveHorizon,
          ).map((v) =>
            durationInfectionYears > 0
              ? Math.min(1, Math.max(0, v / durationInfectionYears))
              : 0,
          ),
        }
      : {}),
    // Per-case treatment cost schedule. For each cycle k (0-indexed JS /
    // 1-indexed R) covering year k+1 at age `cohortStartingAge + k`, this
    // is the additive-over-baseline per-case treatment cost:
    //
    //   per_case[k] = treatmentCostPerCaseEUR
    //              + hospitalization_rate × hospitalizationCostPerCaseEUR
    //              + Σ_c rate_c[k] × (cost_c[k] − treatmentCostPerCaseEUR)
    //
    // Backend (markov_service.R) consumes this as a flat per-case event cost
    // (cases[t] × schedule[t]) at the service layer, replacing the previous
    // state-time `p_inf × (cost_infection + hosp + comp_cost)` heemod
    // expression that was being attenuated by competing background mortality
    // under `method = "life-table"`. Mirrors the QALY Path A treatment.
    //
    // Y1 path: the decision-tree heemod build still emits
    // `cost_infection + hosp_rate × cost_hosp` per case; result_integrator
    // adds the remaining additive complication increment on top using
    // schedule[1].
    ...((): Record<string, number[]> => {
      const baseline = inputs.treatmentCostPerCaseEUR ?? 0;
      const hospRate = inputs.hospitalizationRate ?? 0;
      const hospCost = inputs.hospitalizationCostPerCaseEUR ?? 0;
      const sched = expandPerCaseTreatmentCostToCycles(
        baseline,
        hospRate,
        hospCost,
        inputs.complications,
        effectiveHorizon,
      );
      return sched.length === effectiveHorizon && effectiveHorizon > 0
        ? { per_case_treatment_cost_schedule: sched }
        : {};
    })(),
    // Deprecated: kept for one release so external R tests/scripts that read
    // `complication_cost_schedule` directly do not break the same release as
    // the per-case migration. The new code path consumes
    // `per_case_treatment_cost_schedule` exclusively. Remove once downstream
    // consumers have migrated.
    ...((): Record<string, number[]> => {
      const sched = expandComplicationsToCycles(
        inputs.complications,
        effectiveHorizon,
      );
      return sched.length === effectiveHorizon &&
        effectiveHorizon > 1 &&
        sched.some((v) => v > 0)
        ? { complication_cost_schedule: sched }
        : {};
    })(),
    // Background utility (healthy-state HRQoL) source preset. When non-'none',
    // backend resolves a per-cycle background_utility_schedule from its registry.
    ...(typeof inputs.backgroundUtilitySource === 'string' &&
    inputs.backgroundUtilitySource !== 'none' &&
    inputs.backgroundUtilitySource.length > 0 &&
    typeof inputs.cohortStartingAge === 'number'
      ? {
          background_utility_source: inputs.backgroundUtilitySource,
          starting_age: inputs.cohortStartingAge,
        }
      : {}),
    regional_parameters: REGIONS.filter((r) =>
      inputs.selectedRegions.includes(r.key),
    ).map((r) => ({
      id: r.key,
      name: r.name,
      population: getRegionPopulation(r.key, globalInputs.populationMode) || 0,
      coverage:
        (inputs.coveragePctByRegion[r.key] ?? inputs.defaultCoveragePct) / 100,
    })),
  };
}

/**
 * Calculate average coverage rate across selected regions
 * @param coveragePctByRegion - Coverage percentages by region (e.g., 75 = 75%)
 * @param selectedRegions - Array of selected region keys
 * @param defaultCoveragePct - Default coverage percentage if region not found
 * @returns Average coverage as percentage (e.g., 75 for 75%)
 */
const calculateAverageCoverage = (
  coveragePctByRegion: Record<string, number>,
  selectedRegions: string[],
  defaultCoveragePct: number = 70
): number => {
  if (selectedRegions.length === 0) return defaultCoveragePct;
  
  const values = selectedRegions.map(regionKey => 
    coveragePctByRegion[regionKey] ?? defaultCoveragePct
  );
  const sum = values.reduce((acc, val) => acc + val, 0);
  return sum / values.length; // Return as percentage, not rate
};

/**
 * Sync vaccine parameters to decision tree nodes
 * 
 * Purpose: Automatically update tree node costs/QALYs when vaccine config changes
 * Used for template-based trees where parameters flow from dashboard
 */
export const syncVaccineParametersToTree = (
  vaccineInputs: any,
) => {
  const clientStore = useVaccineClientStore.getState();
  
  // Get current tree
  const currentTree = clientStore.getTreeState();
  if (!currentTree.nodes || Object.keys(currentTree.nodes).length === 0) {
    return; // No tree to update
  }

  // 1. Structure Updates (Mortality & Hospitalization)
  // Flat structure: Infected -> [Death, Hospitalized, Recovered]
  
  let mortalityRate = vaccineInputs.baselineMortalityRate ?? 0.001;
  let hospitalizationRate = vaccineInputs.hospitalizationRate ?? 0.05;

  // Safety check: Ensure sum doesn't exceed 1
  // CRITICAL: This must be done before creating nodes to prevent invalid probabilities
  if (mortalityRate + hospitalizationRate > 1) {
    console.warn('[vaccineFacade] Mortality + Hospitalization > 1, normalizing...');
    const total = mortalityRate + hospitalizationRate;
    mortalityRate = mortalityRate / total;
    hospitalizationRate = hospitalizationRate / total;
  }
  
  // Additional validation: Ensure sum is valid (should be <= 1 after normalization)
  if (mortalityRate + hospitalizationRate > 1) {
    console.error('[vaccineFacade] ERROR: Invalid probability sum after normalization. Setting to safe defaults.');
    mortalityRate = Math.min(mortalityRate, 1);
    hospitalizationRate = Math.min(hospitalizationRate, 1 - mortalityRate);
  }
  
  let nodes = { ...currentTree.nodes };
  let edges = [...(currentTree.edges || [])];
  let structureChanged = false;

  // Helper to find nodes by label/id pattern
  const findNodes = (pattern: string) => Object.values(nodes).filter(n => 
    n.id.includes(pattern.toLowerCase()) || n.label?.toLowerCase().includes(pattern.toLowerCase())
  );

  const infectedNodes = findNodes('Infected').filter(n => {
    const label = n.label?.toLowerCase() || '';
    const id = n.id.toLowerCase();
    return !label.includes('death') && 
           !label.includes('hospitalized') && 
           !label.includes('recovered') && 
           !label.includes('survived') &&
           !id.includes('_death') &&
           !id.includes('_hosp') &&
           !id.includes('_recover') &&
           !id.includes('_survive');
  });
  
  infectedNodes.forEach(infected => {
    // We want to ensure Infected has the correct children based on rates.
    // Children should be: Death (if >0), Hospitalized (if >0), Recovered (always, as remainder)
    
    // Check current children to see if we need to update
    const childrenEdges = edges.filter(e => e.parentId === infected.id);
    const hasDeath = childrenEdges.some(e => nodes[e.childId]?.label === 'Death');
    const hasHosp = childrenEdges.some(e => nodes[e.childId]?.label === 'Hospitalized');
    const hasRecovered = childrenEdges.some(e => nodes[e.childId]?.label === 'Recovered' || nodes[e.childId]?.label === 'Survived');
    
    const needsDeath = mortalityRate > 0;
    const needsHosp = hospitalizationRate > 0;
    
    // If structure doesn't match requirements, rebuild it
    if (hasDeath !== needsDeath || hasHosp !== needsHosp || !hasRecovered) {
       // Robust Deletion: Identify current children to delete
       // We delete ALL children that are Death, Hospitalized, or Recovered/Survived
       // regardless of their ID, because we are about to recreate them.
       const childrenToDelete = childrenEdges
         .map(e => e.childId)
         .filter(id => {
            const label = nodes[id]?.label;
            return label === 'Death' || label === 'Hospitalized' || label === 'Recovered' || label === 'Survived';
         });
         
       childrenToDelete.forEach(id => {
         console.log(`[Debug] Deleting node: ${id} (Label: ${nodes[id]?.label})`);
         if (nodes[id]) delete nodes[id];
       });

       // Clear existing children edges
       edges = edges.filter(e => e.parentId !== infected.id);
       
       const deathId = `${infected.id}_death`;
       const hospId = `${infected.id}_hosp`;
       const recoverId = `${infected.id}_recover`;
       
       const newChildrenIds: string[] = [];
       
      // 1. Death
      if (needsDeath) {
        if (!nodes[deathId]) {
          nodes[deathId] = { 
            id: deathId, 
            type: 'terminal', 
            label: 'Death', 
            nodeType: 'death', // CRITICAL: Set nodeType so backend can identify deaths
            children: [] 
          };
        } else {
          // Update existing node to ensure nodeType is set
          nodes[deathId] = { ...nodes[deathId], nodeType: 'death' };
        }
        edges.push({ parentId: infected.id, childId: deathId, probability: mortalityRate });
        console.log(`[Debug] Added Death edge: ${infected.id} -> ${deathId}`);
        newChildrenIds.push(deathId);
      }
      
      // 2. Hospitalized
      if (needsHosp) {
        if (!nodes[hospId]) {
          nodes[hospId] = { 
            id: hospId, 
            type: 'terminal', 
            label: 'Hospitalized', 
            nodeType: 'hospitalization', // CRITICAL: Set nodeType so backend can identify hospitalizations
            children: [] 
          };
        } else {
          // Update existing node to ensure nodeType is set
          nodes[hospId] = { ...nodes[hospId], nodeType: 'hospitalization' };
        }
        edges.push({ parentId: infected.id, childId: hospId, probability: hospitalizationRate });
        console.log(`[Debug] Added Hosp edge: ${infected.id} -> ${hospId}`);
        newChildrenIds.push(hospId);
      }
      
      // 3. Recovered (Remainder)
      // Only create Recovered if we have other branches (Death/Hosp)
      // If both are 0, Infected becomes terminal.
      
      if (needsDeath || needsHosp) {
        let recoveredProb = 1 - (needsDeath ? mortalityRate : 0) - (needsHosp ? hospitalizationRate : 0);
        if (recoveredProb < 0) recoveredProb = 0; 
        
        if (!nodes[recoverId]) {
          nodes[recoverId] = { 
            id: recoverId, 
            type: 'terminal', 
            label: 'Recovered', 
            nodeType: 'recover', // CRITICAL: Set nodeType for recovered nodes
            children: [] 
          };
        } else {
          // Update existing node to ensure nodeType is set
          nodes[recoverId] = { ...nodes[recoverId], nodeType: 'recover' };
        }
        edges.push({ parentId: infected.id, childId: recoverId, probability: recoveredProb });
        newChildrenIds.push(recoverId);
        
        // Update Infected node to chance
        // Note: Costs will be set in syncVaccineParametersToTree based on user inputs
        // Infected nodes (chance or terminal) should have costs based on branch (vaccinate vs no vaccinate)
        nodes[infected.id] = { 
          ...infected, 
          type: 'chance', 
          nodeType: 'infection', // CRITICAL: Set nodeType so backend can identify infections
          children: newChildrenIds 
        };
      } else {
        // Revert to terminal (no death/hosp branches)
        // We already cleared edges and deleted children above.
        nodes[infected.id] = { 
          ...infected, 
          type: 'terminal', 
          nodeType: 'infection', // CRITICAL: Set nodeType so backend can identify infections
          children: [] 
        };
      }
       
       structureChanged = true;
    }
  });

  if (structureChanged) {
    clientStore.setNodes(nodes);
    clientStore.setEdges(edges);
    // Need to refresh currentTree for value updates
    currentTree.nodes = nodes;
    currentTree.edges = edges;
  }

  // 2. Value Updates (Costs & QALYs)
  // Update costs and QALYs in terminal nodes based on vaccine data
  const treatmentCost = vaccineInputs.treatmentCostPerCaseEUR ?? 200;
  
  // Prefer the backend-computed year-1 VE when available (veByYear[0]).
  // The R engine may apply schedule/booster logic that results in a different
  // effective year-1 VE than the raw vaccineEfficacyPct input.
  // Fall back to the raw input before the first computation.
  const backendResults = useVaccineDashboardStore.getState().backendResults;
  const computedYear1VE = backendResults?.veByYear?.[0];
  const efficacy = typeof computedYear1VE === 'number'
    ? computedYear1VE
    : (vaccineInputs.vaccineEfficacyPct ?? 70) / 100;
  
  // Disease parameters
  const utilityInfection = vaccineInputs.utilityInfection ?? 0.8;
  const durationInfectionYears = (vaccineInputs.durationInfectionDays ?? 7) / 365;
  // Calculate baseline_incidence and derive baselineHealthy (matches backend calculation)
  const baseline_incidence = vaccineInputs.baselineIncidencePer100k != null 
    ? vaccineInputs.baselineIncidencePer100k / 100000 
    : 0.05;
  const baselineHealthy = 1 - baseline_incidence;
  // hospitalizationRate and mortalityRate already used above
  
  // Use updated nodes object (from structure updates) instead of stale currentTree.nodes
  const nodesObj = structureChanged ? nodes : (currentTree.nodes as Record<string, any>);
  const edgesObj = structureChanged ? edges : (currentTree.edges || []);
  
  Object.keys(nodesObj).forEach((nodeId) => {
    const node = nodesObj[nodeId];
    
    // Check if this is an infected node (chance or terminal)
    const isInfectedNode = (nodeId.includes('infected') || nodeId.includes('infection')) && 
                           !nodeId.includes('no_infection') && 
                           !nodeId.includes('healthy');
    
    // Set costs and QALYs for infected nodes (both chance and terminal)
    if (isInfectedNode) {
      let infectedCost = 0;
      
      // Base cost: treatment cost (incurred when infected)
      infectedCost = treatmentCost;
      
      // Note: Vaccine costs (vaccine + admin + adverse events) are NOT included here
      // They are calculated separately by backend as programme costs
      // Healthcare costs should only include disease treatment costs
      
      // Productivity cost is now calculated separately as indirect cost (Task 4f)
      // infectedCost += productivityCostPerCase;
      
      // QALY handling for infected nodes:
      // - If infected node is a chance node (has children like death/recover), QALY = 0
      //   (QALY comes from terminal children: death=0, recover=recovered_value)
      // - If infected node is terminal (no children), QALY = calculated value
      // Check both node.type and outgoing edges to determine if it's a chance node
      const hasOutgoingEdges = edgesObj.some((e: any) => e.parentId === nodeId);
      
      // DEFENSIVE CHECK: If infected node has outgoing edges to death/recover/hosp, it's a chance node
      // and must have QALY = 0 (pure branch point, no time/QALY of its own)
      const outgoingEdges = edgesObj.filter((e: any) => e.parentId === nodeId);
      const hasDeathRecoverHospChildren = outgoingEdges.some((e: any) => {
        const childNode = nodesObj[e.childId];
        const childLabel = childNode?.label?.toLowerCase() || '';
        const childId = e.childId.toLowerCase();
        return childLabel.includes('death') || childLabel.includes('recover') || 
               childLabel.includes('recovered') || childLabel.includes('survived') ||
               childLabel.includes('hospitalized') || childLabel.includes('hosp') ||
               childId.includes('death') || childId.includes('recover') || 
               childId.includes('hosp');
      });
      
      const isChanceNode = node.type === 'chance' || 
                          (node.children && node.children.length > 0) || 
                          hasOutgoingEdges ||
                          hasDeathRecoverHospChildren;
      
      // Force QALY = 0 for infected chance nodes (defensive check)
      const qalyValue = isChanceNode ? 0 : (1 - (durationInfectionYears * (1 - utilityInfection)));
      
      if (isChanceNode && qalyValue !== 0) {
        console.warn(`[QALY FIX] Forcing infected chance node ${nodeId} QALY to 0 (was ${qalyValue})`);
      }
      
      // Update infected node cost and QALY (whether it's chance or terminal)
      clientStore.updateNode(nodeId, { cost: infectedCost, qaly: qalyValue });
    }
    
    if (node.type === 'terminal') {
      const updates: Partial<typeof node> = {};
      
      // Calculate base cost components
      let baseCost = 0;
      
      // Update based on node label/context
      if (nodeId.includes('healthy') || node.label?.includes('Healthy')) {
        // Healthy outcomes: no treatment cost, no productivity loss
        // Note: Vaccine costs are NOT included here - they are calculated separately by backend as programme costs
        // Healthcare costs should only include disease treatment costs
        baseCost = 0;
        updates.cost = baseCost;
        updates.qaly = 1.0; // Perfect health
      } else if (nodeId.includes('hosp') || node.label?.includes('Hospitalized')) {
         // Hospitalized nodes are children of infected chance nodes
         // Hospitalization cost is now configurable via dashboard inputs
         const hospCost = vaccineInputs.hospitalizationCostPerCaseEUR ?? 3000;
         updates.cost = hospCost;
         // Hospitalization QALY uses separate utility and duration parameters
         // If utilityHospitalization = 0, use full infection duration (not just hospitalization duration)
         // to account for the entire period at utility 0
         const durationHospYears = (vaccineInputs.durationHospitalizationDays ?? 5) / 365;
         const durationInfectionYears = (vaccineInputs.durationInfectionDays ?? 7) / 365;
         const utilityHosp = vaccineInputs.utilityHospitalization ?? 0.7;
         // If utility is 0, apply it for the full infection duration, not just hospitalization duration
         // This ensures that when utilityHospitalization = 0, the entire infection period has utility 0
         if (utilityHosp === 0) {
           updates.qaly = 1 - (durationInfectionYears * (1 - utilityHosp));
         } else {
           // Normal case: hospitalization utility applies during hospitalization period
           // Remaining time (infection duration - hospitalization duration) at healthy utility (1.0)
           const remainingTime = Math.max(0, durationInfectionYears - durationHospYears);
           updates.qaly = (durationHospYears * utilityHosp) + (remainingTime * 1.0);
         } 
      } else if (nodeId.includes('death') || node.label?.includes('Death')) {
         // Death nodes are children of infected chance nodes
         // Costs are on the infected parent node, so children have cost = 0
         updates.cost = 0; // Death cost is 0 (Task 3), and treatment costs are on infected parent
         updates.qaly = 0;
      } else if (nodeId.includes('recover') || node.label?.includes('Recovered') || node.label?.includes('Survived')) {
        // Recovered nodes are children of infected chance nodes
        // Costs are on the infected parent node, so children have cost = 0
        updates.cost = 0; // Treatment costs are on infected parent node
        // IMPORTANT: Recovered nodes carry the infection QALY because the parent infected
        // chance node has QALY = 0. This is path-level QALY, not state utility.
        // See R/decision_tree_service.R lines 470-482 for backend rationale.
        const qalyValue = 1 - (durationInfectionYears * (1 - utilityInfection));
        updates.qaly = qalyValue;
      } else if (nodeId.includes('infected') || node.label?.includes('Infected')) {
        // If infected is terminal (no children), costs are already set above for infected nodes
        // Just update QALY here
        const qalyValue = 1 - (durationInfectionYears * (1 - utilityInfection));
        updates.qaly = qalyValue;
      }
      
      if (Object.keys(updates).length > 0) {
        clientStore.updateNode(nodeId, updates);
      }
    } else if (node.type === 'chance' && !isInfectedNode) {
      // CRITICAL: Ensure non-infected chance nodes have cost = 0 (costs only on terminal children)
      // Infected chance nodes have costs set above based on user inputs
      // This prevents heemod from incorrectly including chance node costs for non-infected nodes
      if (node.cost !== 0 && node.cost !== undefined) {
        clientStore.updateNode(nodeId, { cost: 0 });
      }
    }
  });

  // Update edge probabilities based on efficacy and disease rates
  // Note: Mortality and Hospitalization probs are already set during structure update if they were created.
  // But if they existed, we should update them too.
  
  // Update edge probabilities based on efficacy and disease rates
  if (currentTree.edges) {
    const validEdges = currentTree.edges.filter(e => {
      const parent = nodesObj[e.parentId];
      const child = nodesObj[e.childId];
      if (!parent || !child) return false;
      
      if (parent.children && !parent.children.includes(e.childId)) {
        return false;
      }
      return true;
    });
    
    const updatedEdges = validEdges.map((edge) => {
      // 1. Efficacy: Healthy vs Infected (under Vaccinate)
      if (edge.childId?.includes('healthy') && edge.parentId?.includes('vaccinate') && !edge.parentId?.includes('no_vaccinate')) {
        const probHealthy = efficacy + (1 - efficacy) * baselineHealthy;
        return { ...edge, probability: probHealthy };
      } 
      
      if (edge.childId?.includes('infected') && edge.parentId?.includes('vaccinate') && !edge.parentId?.includes('no_vaccinate')) {
        const probHealthy = efficacy + (1 - efficacy) * baselineHealthy;
        return { ...edge, probability: 1 - probHealthy };
      }

      // 2. Baseline Incidence: Healthy vs Infected (under No Vaccinate)
      const childNode = nodesObj[edge.childId];
      
      const isHealthy = 
        childNode?.nodeType === 'healthy' || 
        edge.childId?.includes('healthy') || 
        childNode?.label?.toLowerCase().includes('healthy') || 
        childNode?.label?.toLowerCase().includes('no infection');
        
      if (isHealthy && edge.parentId?.includes('no_vaccinate')) {
        return { ...edge, probability: baselineHealthy };
      }

      const isInfected = 
        childNode?.nodeType === 'infection' || 
        edge.childId?.includes('infected') || 
        edge.childId?.includes('infection') || 
        childNode?.label?.toLowerCase().includes('infected') || 
        childNode?.label?.toLowerCase().includes('infection');
        
      if (isInfected && edge.parentId?.includes('no_vaccinate')) {
        return { ...edge, probability: baseline_incidence };
      }
      
      // 2. Mortality & Hospitalization (Flat)
      if (edge.parentId?.includes('infected')) {
         if (edge.childId?.includes('death') || nodesObj[edge.childId]?.label === 'Death') {
            return { ...edge, probability: mortalityRate };
         } else if (edge.childId?.includes('hosp') || nodesObj[edge.childId]?.label === 'Hospitalized') {
            return { ...edge, probability: hospitalizationRate };
         } else if (edge.childId?.includes('recover') || nodesObj[edge.childId]?.label === 'Recovered') {
            const prob = 1 - (mortalityRate + hospitalizationRate);
            return { ...edge, probability: prob > 0 ? prob : 0 };
         }
      }
      
      return edge;
    });
    
    clientStore.setEdges(updatedEdges);
  }
};

/**
 * Export combined scenario (tree + vaccine config)
 * 
 * Purpose: Save complete scenario for JSON/CSV export (Week 3)
 */
export const exportCombinedScenario = () => {
  const treeState = useVaccineClientStore.getState().getTreeState();
  const dashboardInputs = useVaccineDashboardStore.getState().inputs;
  const audience = useVaccineDashboardStore.getState().audience;
  const disease = useVaccineDashboardStore.getState().disease;

  return {
    version: '1.0.0',
    timestamp: new Date().toISOString(),
    tree: treeState,
    vaccine: {
      audience,
      disease,
      inputs: dashboardInputs,
    },
  };
};

/**
 * Import combined scenario (tree + vaccine config)
 * 
 * Purpose: Load complete scenario from JSON/CSV import (Week 3)
 */
export const importCombinedScenario = (scenario: any) => {
  if (!scenario || !scenario.tree || !scenario.vaccine) {
    throw new Error('Invalid scenario format');
  }

  // Update decision tree store
  const clientStore = useVaccineClientStore.getState();
  clientStore.setNodes(scenario.tree.nodes || {});
  clientStore.setEdges(scenario.tree.edges || []);
  if (scenario.tree.rootId) {
    clientStore.setRootId(scenario.tree.rootId);
  }

  // Update vaccine dashboard store
  const dashboardStore = useVaccineDashboardStore.getState();
  dashboardStore.patchInputs(scenario.vaccine.inputs);
  if (scenario.vaccine.audience) {
    dashboardStore.setAudience(scenario.vaccine.audience);
  }
  if (scenario.vaccine.disease) {
    dashboardStore.setDisease(scenario.vaccine.disease);
  }
};

/**
 * Prepare computation by syncing vaccine parameters to decision tree store
 * 
 * This function ensures the decision tree store has the latest vaccine
 * parameters before computation. Call this before triggering computation.
 * 
 * @returns true if ready to compute, false if data is invalid
 */
export function prepareComputationData(): boolean {
  const vaccineState = useVaccineDashboardStore.getState();
  const inputs = vaccineState.inputs;
  
  // Validate vaccine inputs
  if (inputs.vaccineCostPerDoseEUR == null || inputs.vaccineCostPerDoseEUR < 0) {
    console.warn('[vaccineFacade] Invalid vaccine cost');
    return false;
  }
  
  if (inputs.treatmentCostPerCaseEUR == null || inputs.treatmentCostPerCaseEUR < 0) {
    console.warn('[vaccineFacade] Invalid treatment cost');
    return false;
  }

  // Force sync of node costs and probabilities to ensure tree matches current inputs
  // This fixes the "Nothing Changed" issue by updating existing nodes
  syncVaccineParametersToTree(inputs);
  
  // Update cost parameters in tree store
  useVaccineClientStore.setState({
    costParameters: {
      cost_vaccine_dose: inputs.vaccineCostPerDoseEUR ?? 0,
      cost_admin: inputs.adminCostPerDoseEUR ?? 0, // No admin cost in vaccine dashboard
      cost_infection: inputs.treatmentCostPerCaseEUR ?? 0,
      cost_hospitalization: inputs.hospitalizationCostPerCaseEUR ?? 3000,
      cost_productivity_per_case: inputs.perspective === 'societal' 
        ? (inputs.productivityValuePerDayEUR * inputs.productivityLossDaysPerCase) 
        : 0,
      cost_adverse_event: inputs.adverseEventCost ?? 0,
      efficacy: (inputs.vaccineEfficacyPct ?? 70) / 100,
      adverse_event_rate: inputs.adverseEventRate ?? 0,
      adverse_event_qaly_loss : inputs.adverseEventQalyLoss ?? 0,
      doses_per_course : inputs.dosesPerCourse || 1,
      discount_vaccine_cost: (() => {
        const discountPct = inputs.vaccinePriceDiscountPct ?? 0;
        if (discountPct < 0 || discountPct > 100) {
          console.warn('[vaccineFacade] Invalid discount percentage, clamping to 0-100%');
          return 0;
        }
        return discountPct / 100;
      })(),
      booster_cost: inputs.boosterCost ?? 0, // Multi-year: booster cost (≥ 0) - map camelCase to snake_case
    },
  });
  
  // Update population parameters via shared builder so optional schedule fields
  // (ve_schedule / incidence_schedule / qaly_loss_schedule / background_utility_source)
  // can never silently drop on their way to the backend.
  useVaccineClientStore.setState({
    populationParameters: buildPopulationParameters(inputs, vaccineState.globalInputs),
  });
  const effectiveHorizonSync = resolveEffectiveTimeHorizon(inputs);
  useVaccineClientStore.setState({
    simulationParameters: {
      cycles: effectiveHorizonSync,
      willingness_to_pay: 20000, // Default
      discount_rate: (inputs.discountRatePct ?? 3) / 100, // Convert percentage to rate
      discounting_enabled: effectiveHorizonSync > 1 && (inputs.discountingEnabled ?? false), // Multi-year: only enabled when H > 1
    }
  });
  
  return true;
}

/**
 * Sync decision tree parameters back to vaccine dashboard
 * 
 * This function performs reverse sync - when parameters are edited
 * in the decision tree's ParameterInputPanel, this updates the
 * vaccine dashboard store so values persist across tab switches.
 * 
 * Call this when switching away from tree tab or when tree parameters change.
 */
export function syncTreeParametersToVaccine(): void {
  const tree = useVaccineClientStore.getState();
  const cost = tree.costParameters;
  const pop = tree.populationParameters;
  const sim = tree.simulationParameters;

  // Don't sync if parameters are undefined/empty (tree not initialized)
  if (!cost || !pop) {
    return;
  }
  
  // Build a patch for dashboard.inputs - single immutable update
  const inputsPatch: Partial<VaccineInputs> = {};
  
  // Sync cost parameters
  if (typeof cost.cost_vaccine_dose === "number") {
    inputsPatch.vaccineCostPerDoseEUR = cost.cost_vaccine_dose;
  }
  if (typeof cost.cost_admin === "number") {
    inputsPatch.adminCostPerDoseEUR = cost.cost_admin;
  }
  if (typeof cost.cost_infection === "number") {
    inputsPatch.treatmentCostPerCaseEUR = cost.cost_infection;
  }
  
  // Sync efficacy (robust to string/number, convert from decimal to percentage)
  // Tree: costParameters.efficacy is decimal (e.g. 0.77)
  // Dashboard: vaccineEfficacyPct is percent (e.g. 77)
  const effDec = Number((cost as any)?.efficacy);
  if (Number.isFinite(effDec)) {
    inputsPatch.vaccineEfficacyPct = Math.round(effDec * 100);
  }
  
  // Sync population parameters
  if (typeof pop.population_size === "number") {
    inputsPatch.population = pop.population_size;
  }
  
  // Sync coverage rate (convert back to percentage and update all regions)
  if (typeof pop.coverage_rate === "number") {
    const coveragePercentage = pop.coverage_rate * 100;
    const dashboardState = useVaccineDashboardStore.getState();
    const currentCoverage = dashboardState.inputs.coveragePctByRegion;
    const regions = Object.keys(currentCoverage);
    inputsPatch.coveragePctByRegion = regions.reduce((acc, region) => ({
      ...acc,
      [region]: coveragePercentage,
    }), {} as Record<string, number>);
  }
  
  // Sync baseline incidence (convert back to per 100k)
  if (typeof pop.baseline_incidence === "number") {
    inputsPatch.baselineIncidencePer100k = pop.baseline_incidence * 100000;
  }

  // Sync multi-year parameters
  if (sim) {
    if (typeof sim.cycles === "number") {
      inputsPatch.timeHorizonYears = sim.cycles;
    }
    if (typeof sim.discounting_enabled === "boolean") {
      inputsPatch.discountingEnabled = sim.discounting_enabled;
    }
  }
  if (typeof (pop as any).waning_rate === "number") {
    inputsPatch.waningPctPerYear = (pop as any).waning_rate * 100;
  }
  if (Array.isArray((pop as any).booster_years)) {
    inputsPatch.boosterYears = (pop as any).booster_years;
  }
  if (typeof (cost as any).booster_cost === "number") {
    inputsPatch.boosterCost = (cost as any).booster_cost;
  }

  // Single immutable update to nested inputs
  if (Object.keys(inputsPatch).length > 0) {
    useVaccineDashboardStore.setState((s) => ({
      ...s,
      inputs: { ...s.inputs, ...inputsPatch },
    }));
  }
}


export { useVaccineClientStore, useVaccineDashboardStore };
